import {
  put, takeLatest, takeEvery, call,
} from 'redux-saga/effects';
import { accountingsActions } from 'src/store/accountings/accountings.actions';
import { backendUrl } from 'src/config';
import { format } from '../../utils/date.utils';
import { formatAmount } from '../../routes/Accountings/util';
import axiosInstance, { axiosInstanceLongTimeout } from 'src/axios-instance';

function* getPostedAccountingByDate(action) {
  try {
    const accountings = yield axiosInstance.request({
      method: 'GET',
      url: `${backendUrl}/api/posted_slaughter_by_date/${action.payload.date}?settlement_no=${action.payload.settlement_no}`,
      withCredentials: true,
    });

    yield put(
      accountingsActions.fetchPostedByDateSuccess({
        date: action.payload.date,
        settlement_no: action.payload.settlement_no,
        slaughter_data: accountings.data.posted_slaughter_by_date,
      }),
    );
  } catch (error) {
    yield put(accountingsActions.fetchPostedbyDateFailure({ errorcodes: [] }));
  }
}

function* getTempAccountingByDate(action) {
  try {
    const accountings = yield axiosInstance.request({
      method: 'GET',
      url: `${backendUrl}/api/temp_slaughter_by_date/${action.payload.date}`,
      withCredentials: true,
    });

    yield put(
      accountingsActions.fetchTempByDateSuccess({
        date: action.payload.date,
        slaughter_data: accountings.data.temp_slaughter_by_date,
      }),
    );
  } catch (error) {
    yield put(accountingsActions.fetchTempbyDateFailure({ errorcodes: [] }));
  }
}

function* getPostedSlaughtes() {
  try {
    const list = yield axiosInstance.request({
      method: 'GET',
      url: `${backendUrl}/api/posted_slaughters_list`,
      withCredentials: true,
    });

    yield put(
      accountingsActions.fetchPostedSlaughterListSuccess({
        list: list.data.posted_slaughters_list,
      }),
    );
  } catch (error) {
    yield put(
      accountingsActions.fetchPostedSlaughterListFailure({ errorcodes: [] }),
    );
  }
}

function* getTempSlaughters() {
  try {
    const dates = yield axiosInstance.request({
      method: 'GET',
      url: `${backendUrl}/api/temp_slaughters_list`,
      withCredentials: true,
    });

    yield put(
      accountingsActions.fetchTempSlaughterListSuccess({
        list: dates.data.temp_slaughters_list,
      }),
    );
  } catch (error) {
    yield put(
      accountingsActions.fetchTempSlaughterListFailure({ errorcodes: [] }),
    );
  }
}

function* getDocuments() {
  try {
    const getAmountPart = (amount) => (amount ? ` / kr ${formatAmount(amount)}` : '');
    const documents = yield call(() => axiosInstance
      .request({
        method: 'GET',
        url: `${backendUrl}/api/documents`,
        withCredentials: true,
      })
      .then(({ data }) => {
        const formatted = (doc) => ({
          ...doc,
          formatted: `${format(new Date(doc.date), 'dd.MM.yyyy')} / ${
              doc.document_no
              }${getAmountPart(doc.amount)}`,
        });
        const invoices = data.invoices.map((doc) => formatted(doc));
        const creditNotes = data.credit_notes.map((doc) => formatted(doc));
        const accountings = data.accountings.map((doc) => formatted(doc));
        return {
          ...data,
          invoices,
          credit_notes: creditNotes,
          accountings,
          open_posts: data.open_posts.map((doc) => ({
            ...doc,
            formatted: `${format(new Date(doc.date), 'dd.MM.yyyy')}`,
          })),
          withdrawals: data.withdrawals.map((doc) => ({
            ...doc,
            formatted: `${format(new Date(doc.date), 'dd.MM.yyyy')}`,
          })),
        };
      }));

    yield put(
      accountingsActions.fetchDocumentsSuccess({
        documents,
      }),
    );
  } catch (error) {
    yield put(accountingsActions.fetchDocumentsFailure({ error }));
  }
}

function* getDashboardStatistics() {
  try {
    const statistics = yield call(() => axiosInstance
      .request({
        method: 'GET',
        url: `${backendUrl}/api/farmer_statistics/dashboard`,
        withCredentials: true,
      })
      .then(({ data }) => {
        if (data) {
          return data;
        }
      }));

    yield put(
      accountingsActions.fetchDashboardStatisticsSuccess({
        statistics,
        isFetchingDashboard: false,
        isFetching: true,
      }),
    );
  } catch (error) {
    console.error(error);
    yield put(accountingsActions.fetchDashboardStatisticsFailure({ error }));
  }
}

function* getSlaughterhouseStatistics() {
  try {
    const statistics = yield call(() => axiosInstance
      .request({
        method: 'GET',
        url: `${backendUrl}/api/slaughterhouse_statistics`,
        withCredentials: true,
      })
      .then(({ data }) => {
        if (data) {
          return data;
        }
      }));

    yield put(
      accountingsActions.fetchSlaughterhouseStatisticsSuccess({
        statistics: statistics.statistics,
        isFetchingDashboard: false,
        isFetching: true,
      }),
    );
  } catch (error) {
    console.error(error);
    yield put(
      accountingsActions.fetchSlaughterhouseStatisticsFailure({ error }),
    );
  }
}

function* getFarmerStatistics(action) {
  const { fetchAll } = action.payload;
  try {
    const statistics = yield call(() => axiosInstanceLongTimeout
      .request({
        method: 'GET',
        url: `${backendUrl}/api/farmer_statistics/${action.payload.timeFrame}/${action.payload.kind}/${action.payload.subkind}`,
        withCredentials: true,
      })
      .then(({ data }) => {
        if (data) {
          return data;
        }
      }));

    if (fetchAll) {
      yield put(
        accountingsActions.fetchAllFarmerStatisticsSuccess({
          data: statistics.farmer_statistics,
        }),
      );
    } else {
      yield put(
        accountingsActions.fetchFarmerStatisticsSuccess({
          statistics,
          timeFrame: action.payload.timeFrame,
          subkind: action.payload.subkind,
          kind: action.payload.kind,
          isFetchingDashboard: false,
          isFetching: true,
        }),
      );
    }

    if (action.payload.onSuccess) {
      action.payload.onSuccess();
    }
  } catch (error) {
    console.error(error);
    yield put(accountingsActions.fetchFarmerStatisticsFailure({ error }));
  }
}

function* getAnimalKindsWithStats(action) {
  try {
    const animalKindsWithStats = yield call(() => axiosInstance
      .request({
        method: 'GET',
        url: `${backendUrl}/api/farmer_statistics/animal_kinds_with_stats`,
        withCredentials: true,
      })
      .then(({ data }) => {
        if (data) {
          return data;
        }
      }));

    yield put(
      accountingsActions.fetchAnimalKindsWithStatsSuccess({
        animalKindsWithStats,
      }),
    );
    if (action?.payload?.onSuccess) {
      action.payload.onSuccess(animalKindsWithStats);
    }
  } catch (error) {
    console.error(error);
    yield put(accountingsActions.fetchAnimalKindsWithStatsFailure({ error }));
  }
}

export default [
  takeLatest(
    accountingsActions.fetchPostedByDate().type,
    getPostedAccountingByDate,
  ),
  takeLatest(
    accountingsActions.fetchTempByDate().type,
    getTempAccountingByDate,
  ),
  takeLatest(
    accountingsActions.fetchPostedSlaughterList().type,
    getPostedSlaughtes,
  ),
  takeLatest(
    accountingsActions.fetchTempSlaughterList().type,
    getTempSlaughters,
  ),
  takeLatest(accountingsActions.fetchDocuments().type, getDocuments),
  takeLatest(
    accountingsActions.fetchDashboardStatistics().type,
    getDashboardStatistics,
  ),
  takeEvery(
    accountingsActions.fetchFarmerStatistics().type,
    getFarmerStatistics,
  ),
  takeEvery(
    accountingsActions.fetchAllFarmerStatistics().type,
    getFarmerStatistics,
  ),
  takeLatest(
    accountingsActions.fetchAnimalKindsWithStats().type,
    getAnimalKindsWithStats,
  ),
  takeLatest(
    accountingsActions.fetchSlaughterhouseStatistics().type,
    getSlaughterhouseStatistics,
  ),
];
